import * as THREE from "three";
import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";

/**
 * Renders Rapier3D collider wireframes into the Three.js scene.
 * Toggle with PhysicsDebugRenderer.setEnabled(true/false).
 * Press P in-game to toggle.
 */
export class PhysicsDebugRenderer
{
  private static enabled = false;
  private static lines: THREE.LineSegments | null = null;
  private static geometry: THREE.BufferGeometry | null = null;
  private static material: THREE.LineBasicMaterial | null = null;

  public static setEnabled( value: boolean ): void
  {
    this.enabled = value;

    if ( value )
    {
      if ( !this.geometry ) this.geometry = new THREE.BufferGeometry();
      if ( !this.material )
      {
        this.material = new THREE.LineBasicMaterial( {
          vertexColors: true,
          depthTest: false,
          depthWrite: false,
          transparent: true,
        } );
      }
      if ( !this.lines )
      {
        this.lines = new THREE.LineSegments( this.geometry, this.material );
        this.lines.renderOrder = 9999;
        this.lines.frustumCulled = false;
      }
      SceneManager.world.add( this.lines );
      console.log( "[PhysicsDebugRenderer] enabled" );
    }
    else if ( this.lines )
    {
      SceneManager.world.remove( this.lines );
      console.log( "[PhysicsDebugRenderer] disabled" );
    }
  }

  public static isEnabled(): boolean
  {
    return this.enabled;
  }

  /** Call once per frame, after PhysicsWorld.update(). */
  public static update(): void
  {
    if ( !this.enabled || !this.lines || !this.geometry ) return;

    const { vertices, colors } = PhysicsWorld.getWorld().debugRender();

    // Rapier returns RGBA (4 components) per vertex. Three.js LineBasicMaterial
    // with vertexColors expects RGB (3 components). We strip the alpha channel.
    const vertex_count = vertices.length / 3;
    const rgb = new Float32Array( vertex_count * 3 );
    for ( let i = 0; i < vertex_count; i++ )
    {
      rgb[ i * 3 ]     = colors[ i * 4 ];
      rgb[ i * 3 + 1 ] = colors[ i * 4 + 1 ];
      rgb[ i * 3 + 2 ] = colors[ i * 4 + 2 ];
    }

    const pos_attr = new THREE.BufferAttribute( vertices, 3 );
    const col_attr = new THREE.BufferAttribute( rgb, 3 );
    pos_attr.needsUpdate = true;
    col_attr.needsUpdate = true;

    this.geometry.setAttribute( "position", pos_attr );
    this.geometry.setAttribute( "color", col_attr );
    this.geometry.computeBoundingSphere();
  }

  public static dispose(): void
  {
    this.setEnabled( false );
    this.geometry?.dispose();
    this.material?.dispose();
    this.geometry = null;
    this.material = null;
    this.lines = null;
  }
}
